import React, { useMemo, useState } from 'react'
import { Box, Paper, Typography, Avatar, IconButton } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

type Device = {
  id: string
  name: string
  vendor?: string
  models: number
  color?: string
}

const sampleDevices: Device[] = [
  { id: 'ecg-01', name: 'ECG Monitor', vendor: 'PulseTech', models: 5, color: '#1976d2' },
  { id: 'bp-01', name: 'Blood Pressure', vendor: 'CareHealth', models: 3, color: '#0b8457' },
  { id: 'ox-01', name: 'Pulse Oximeter', vendor: 'OxPro', models: 4, color: '#b08900' },
  { id: 'glu-01', name: 'Glucometer', vendor: 'GlucoSafe', models: 2, color: '#8e24aa' },
  { id: 'therm-01', name: 'Thermometer', vendor: 'HeatSense', models: 6, color: '#d84315' },
  { id: 'weight-01', name: 'Weight Scale', vendor: 'WeighRight', models: 1, color: '#455a64' },
]

export default function DevicesPanel(): React.ReactElement {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sampleDevices
    return sampleDevices.filter(d => d.name.toLowerCase().includes(q) || (d.vendor || '').toLowerCase().includes(q))
  }, [query])

  const badgeText = (models: number) => {
    if (models >= 5) return `0${models}+ devices`
    return `${models} device${models > 1 ? 's' : ''}`
  }

  return (
    <Box sx={{  }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            Device Catalog
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supported health devices and models
          </Typography>
        </Box>

        <Box sx={{ ml: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              px: 1,
              py: '2px',
            }}
          >
            <SearchIcon fontSize="small" color="action" />
            <input
              placeholder="Search devices"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                marginLeft: 8,
                minWidth: 180,
                background: 'transparent',
                fontSize: 14,
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2, display: 'grid', gap: "20px", gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' } }}>
        {filtered.map(device => (
          <Paper
            key={device.id}
            elevation={1}
            sx={{
              p: 2,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              minHeight: 96,
            }}
          >
            <Avatar sx={{ bgcolor: device.color || '#1976d2', width: 56, height: 56 }}>
              {device.name.split(' ').map(s => s[0]).slice(0, 2).join('')}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" component="div">
                {device.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {device.vendor} • {device.models} model{device.models > 1 ? 's' : ''}
              </Typography>
            </Box>

            <IconButton aria-label="open" size="small">
              <ChevronRightIcon />
            </IconButton>

            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 1.25,
                py: 0.25,
                borderRadius: 1,
                fontSize: 12,
              }}
            >
              {badgeText(device.models)}
            </Box>
          </Paper>
        ))}

        {filtered.length === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">No devices found.</Typography>
          </Paper>
        )}
      </Box>
    </Box>
  )
}
